import { ActivityRow } from './components/activity/BrandActivityCard';

/**
 * Parse a date robustly from various formats:
 * - ISO string (e.g., "2026-09-28T12:00:00.000Z")
 * - Firestore Timestamp object (e.g., { _seconds: 1695686400 } or { seconds: 1695686400 })
 * - Date object
 * - Fallback to new Date(string) for other formats
 */
function parseDate(dateInput: string | number | Date | { _seconds?: number; seconds?: number } | null | undefined): Date | null {
  if (!dateInput) return null;

  // Handle Firestore Timestamp objects
  if (typeof dateInput === 'object' && !('toISOString' in dateInput)) {
    const timestamp = dateInput as { _seconds?: number; seconds?: number };
    if (timestamp._seconds !== undefined) {
      return new Date(timestamp._seconds * 1000);
    }
    if (timestamp.seconds !== undefined) {
      return new Date(timestamp.seconds * 1000);
    }
  }

  // Handle Date objects
  if (dateInput instanceof Date) {
    return dateInput;
  }

  // Handle ISO strings and other string formats
  if (typeof dateInput === 'string') {
    const date = new Date(dateInput);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Handle numbers (milliseconds timestamps)
  if (typeof dateInput === 'number') {
    return new Date(dateInput);
  }

  return null;
}

/**
 * Extract the date from an activity row.
 * Assumes the row has a `when` field that should be a parsed date string.
 * This function attempts to reverse-parse the date from the `when` field.
 * In practice, we'll pass the raw date value instead.
 */
export interface ActivityRowWithRawDate extends Omit<ActivityRow, 'when'> {
  rawDate?: string | number | Date | { _seconds?: number; seconds?: number } | null;
  when: string;
}

/**
 * Sort activity rows by date, newest first.
 * Entries with no date go last.
 * Handles mixed date formats (ISO strings, Firestore Timestamps, Date objects).
 */
export function sortActivityRowsByDate(rows: ActivityRowWithRawDate[]): ActivityRow[] {
  const rowsWithParsedDates = rows
    .map((row) => ({
      ...row,
      parsedDate: row.rawDate ? parseDate(row.rawDate) : null,
    }))
    .sort((a, b) => {
      // Both have dates: sort by date, newest first
      if (a.parsedDate && b.parsedDate) {
        return b.parsedDate.getTime() - a.parsedDate.getTime();
      }

      // Only a has a date: a comes first
      if (a.parsedDate && !b.parsedDate) {
        return -1;
      }

      // Only b has a date: b comes first
      if (!a.parsedDate && b.parsedDate) {
        return 1;
      }

      // Neither has a date: maintain original order
      return 0;
    })
    // Drop the sort key and the raw date: ActivityRow carries neither.
    .map(({ parsedDate, rawDate, ...row }) => row);

  return rowsWithParsedDates;
}
